import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Space, Spin } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import FileSaver from 'file-saver';
import XLSX from 'xlsx';
import { useConnection, useUserInfo } from '../../../../connection/Application';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { useQueryParam } from 'use-query-params';
import { DownloadOutlined } from '@ant-design/icons';

const convertHtmlToPlainText = (html: any) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const userFeedbackHeaders = [
  {
    label: 'EPF Number',
    key: 'epfNo',
  },
  { label: 'First Name', key: 'firstName' },
  { label: 'Last Name', key: 'lastName' },
  {
    label: 'Email',
    key: 'email',
  },
  {
    label: 'Grade',
    key: 'grade',
  },
  {
    label: 'Strengths of the Employee',
    key: 'strengthsOfThEmployee',
    width: 200,
  },
  {
    label: 'Areas to be Improved',
    key: 'areasNeededToBeImproved',
    width: 200,
  },
  {
    label: 'Individual Development Plan',
    key: 'individualDevelopmentPlan',
    width: 200,
  },
  {
    label: 'Overall Comments by Supervisor',
    key: 'overallCommentsBySupervisor',
    width: 200,
  },
  {
    label: 'User Feedback',
    key: 'userFeedback',
    width: 200,
  },
  {
    label: 'Inhibiting Factors',
    key: 'inhibitingFactors',
    width: 200,
  },
  {
    label: 'Facilitating Factors',
    key: 'facilitatingFactors',
    width: 200,
  },
];

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const fileExtension = '.xlsx';

const createXlsx = (csvData: any[], headers: any[], fileName: string, wscols: any[]) => {
  const h = Object.fromEntries(headers.map((e) => [e.key, e.label]));
  const ws = XLSX.utils.json_to_sheet([h], {
    header: headers.map((e) => e.key),
    skipHeader: true,
    // origin: 0, //ok
  });
  ws['!cols'] = wscols;
  XLSX.utils.sheet_add_json(
    ws,
    csvData.map((s) =>
      Object.fromEntries(
        Object.entries(s)
          .map(([key, val]) => ({ key, val }))
          .filter((e) => headers.find((n) => n.key === e.key))
          .map((x) => [x.key, x.val]),
      ),
    ),
    {
      header: headers.map((e) => e.key),
      skipHeader: true,
      origin: -1, //ok
    },
  );
  const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};

export const AllUserFeedBack: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [query] = useState<any>({});
  const [performanceCycles, setPerformanceCycles] = useState<any[]>([]);
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const cycleRef = useRef(cycle);
  const user2 = useUserInfo();
  const canExportFeedback =
    user2?.roles?.includes('admin') ||
    user2?.roles?.includes('hr') ||
    user2?.roles?.includes('manger') ||
    user2?.isLeader;

  const connection = useConnection();

  useEffect(() => {
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setPerformanceCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  const exportUserFeedback = useCallback(
    (query: any) => {
      setLoading(true);
      setCsvLoading(true);
      if (cycle) {
        connection
          .get(`users/all/feedback/${cycle}`, { ...query, exportValue: true })
          .then((value) => {
            const csvData = value.data.map((e: { user: any; teams: any }) => {
              const x = e?.teams?.[0];
              return {
                ...e.user,
                userFeedback: !x ? 'Pending' : x?.userFeedback ?? '',
                inhibitingFactors: !x
                  ? 'Pending'
                  : convertHtmlToPlainText(x?.inhibitingFactors ?? ''),
                facilitatingFactors: !x
                  ? 'Pending'
                  : convertHtmlToPlainText(x?.facilitatingFactors ?? ''),
                grade: !x ? 'Pending' : x?.grade ?? '',
                strengthsOfThEmployee: !x ? 'Pending' : x?.strengthsOfThEmployee ?? '',
                areasNeededToBeImproved: !x ? 'Pending' : x?.areasNeededToBeImproved ?? '',
                individualDevelopmentPlan: !x ? 'Pending' : x?.individualDevelopmentPlan ?? '',
                overallCommentsBySupervisor: !x ? 'Pending' : x?.overallCommentsBySupervisor ?? '',
              };
            });
            console.log(csvData);
            const wscols = userFeedbackHeaders.map((e) => ({
              wch: Math.max(
                e.label.length + 2,
                Math.max(...csvData.map((d: any) => (d[e.key] + '   ').length)),
              ),
            }));
            createXlsx(
              csvData,
              userFeedbackHeaders,
              performanceCycles.find((e) => e.id == cycle).name + '_Users_Feedbacks',
              wscols,
            );
          })
          .finally(() => {
            setLoading(false);
            setCsvLoading(false);
          });
      } else {
        setLoading(false);
        setCsvLoading(false);
      }
    },
    [connection, cycle, performanceCycles],
  );

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Feedbacks"
        subTitle="View employee feedbacks"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {performanceCycles && (
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => {
                setCycle(value, 'replace');
              }}
              data={performanceCycles}
            />
          )}
        </WebUxPageItem>
        <WebUxPageItem>
          <Spin spinning={loading}>
            <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
              {/*<FlexBox horizontalAlign={'left'} style={{ justifyContent: 'space-between' }}>*/}
              {/*  {!loading && (user2?.roles?.includes('admin') || user2?.roles?.includes('hr')) ? (*/}
              {/*    <Select*/}
              {/*      placeholder="Select Team"*/}
              {/*      style={{ width: '200px' }}*/}
              {/*      onMouseDown={(e) => e.preventDefault()}*/}
              {/*      onChange={(val) => setDepartment(val)}*/}
              {/*      defaultValue={department}>*/}
              {/*      <Select.Option key={0} value={0}>*/}
              {/*        All*/}
              {/*      </Select.Option>*/}
              {/*      {departments.map((item) => (*/}
              {/*        <Select.Option key={item.id} value={item.id}>*/}
              {/*          {item.name}*/}
              {/*        </Select.Option>*/}
              {/*      ))}*/}
              {/*    </Select>*/}
              {/*  ) : (*/}
              {/*    <div></div>*/}
              {/*  )}*/}
              {/*  <Space>*/}
              {/*    <Button*/}
              {/*      onClick={() => {*/}
              {/*        setDepartment(undefined);*/}
              {/*        setQuery(undefined);*/}
              {/*      }}>*/}
              {/*      Reset*/}
              {/*    </Button>*/}
              {/*  </Space>*/}
              {/*</FlexBox>*/}
              <FlexBox
                horizontalAlign={'center'}
                verticalAlign={'center'}
                style={{ minHeight: 'calc(100vh - 370px)' }}>
                <div>
                  {canExportFeedback && (
                    <>
                      <Button
                        icon={<DownloadOutlined />}
                        size={'large'}
                        loading={csvLoading}
                        key="1"
                        style={{ padding: '30px !important' }}
                        type="primary"
                        onClick={() => exportUserFeedback(query)}>
                        Export Feedback Data
                      </Button>
                    </>
                  )}
                </div>
              </FlexBox>
            </Space>
          </Spin>
        </WebUxPageItem>
      </WebUxPageContent>
    </WebUxPage>
  );
};
